import { useCallback, useEffect, useRef, useState } from 'react'
import { fetchNotifications, fetchNotificationPreferences, saveNotificationPreferences, markNotificationRead, markAllNotificationsRead } from '../api/notifications'
import type { NotificationFeed, NotificationPreferences } from '../api/notifications'

const defaultPrefs: NotificationPreferences = {sound_enabled:false, sound_volume:35, global_enabled:true}

function playChime(volume: number) {
  try {
    const AudioContextCtor = window.AudioContext
    if (!AudioContextCtor) return
    const ctx = new AudioContextCtor()
    if (ctx.state === 'suspended') { void ctx.close(); return }
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()
    osc.type = 'sine'
    osc.frequency.setValueAtTime(740, ctx.currentTime)
    osc.frequency.exponentialRampToValueAtTime(990, ctx.currentTime + 0.11)
    gain.gain.setValueAtTime(0.0001, ctx.currentTime)
    gain.gain.exponentialRampToValueAtTime(Math.max(0.0001, volume / 100 * 0.055), ctx.currentTime + 0.02)
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.25)
    osc.connect(gain); gain.connect(ctx.destination)
    osc.start(); osc.stop(ctx.currentTime + 0.26)
    osc.onended = () => { void ctx.close() }
  } catch { /* browser may block sound */ }
}

export function useAcademyNotifications() {
  const [feed, setFeed] = useState<NotificationFeed>({unread_count:0, items:[]})
  const [prefs, setPrefs] = useState<NotificationPreferences>(defaultPrefs)
  const initialized = useRef(false)
  const newestId = useRef(0)
  const preferencesRef = useRef(prefs)
  preferencesRef.current = prefs

  const reload = useCallback(async (maySound = false) => {
    try {
      const next = await fetchNotifications()
      const latest = Math.max(0, ...next.items.map(item => item.id))
      if (maySound && initialized.current && latest > newestId.current && next.items.some(item => item.id > newestId.current && !item.is_read) && preferencesRef.current.sound_enabled) {
        playChime(preferencesRef.current.sound_volume)
      }
      newestId.current = Math.max(newestId.current, latest)
      initialized.current = true
      setFeed(next)
    } catch (error) { console.error('Notification sync failed', error) }
  }, [])

  useEffect(() => {
    void fetchNotificationPreferences().then(setPrefs).catch(console.error)
    void reload()
    const stream = new EventSource('/api/v1/notifications/stream', {withCredentials:true})
    stream.addEventListener('notification', () => { void reload(true) })
    const onFocus = () => { void reload(false) }
    window.addEventListener('focus', onFocus)
    const interval = window.setInterval(() => { void reload(false) }, 120000)
    return () => { stream.close(); window.removeEventListener('focus', onFocus); window.clearInterval(interval) }
  }, [reload])

  const read = useCallback(async (id:number) => { await markNotificationRead(id); await reload() }, [reload])
  const readAll = useCallback(async () => { await markAllNotificationsRead(); await reload() }, [reload])
  const updatePrefs = useCallback(async (next:NotificationPreferences) => {
    const saved = await saveNotificationPreferences(next)
    setPrefs(saved)
    await reload()
  }, [reload])
  return {feed, prefs, read, readAll, updatePrefs, reload}
}
